import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/api";
import { ArrowLeft, Save, Pencil, X } from "lucide-react";

const PAYMENT_TYPES = ["Advance", "Part"];

export default function ManagePayment() {
  const { opfId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Read-only, populated from the deal — never entered by hand
  const [leadId, setLeadId] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [advanceTotal, setAdvanceTotal] = useState(0);
  const [partTotal, setPartTotal] = useState(0);
  const [balance, setBalance] = useState(0);

  // Which existing row is currently being edited (by its id)
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingRow, setSavingRow] = useState(false);

  const [form, setForm] = useState({
    date: "",
    mode: "Cheque",
    modeDetails: "",
    paymentType: "Advance",
    amount: "",
  });

  useEffect(() => {
    fetchSummary();
  }, [opfId]);

  const fetchSummary = async () => {
    try {
      const res = await api.get(`/payment-manage/summary/${opfId}`);
      const data = res.data.data || {};

      setLeadId(data.lead_id ?? "");
      setTotalAmount(data.total_amount ?? 0);
      setAdvanceTotal(data.advance_total ?? 0);
      setPartTotal(data.part_total ?? 0);
      setBalance(data.balance ?? 0);
      setRows(data.entries || []);
    } catch (err) {
      console.error(err);
    }
  };

  const submit = async () => {
    if (!form.date || !form.amount) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setLoading(true);

      await api.post("/payment-manage/add", {
        opf_id: opfId,
        lead_id: leadId,
        date: form.date,
        mode: form.mode,
        mode_details: form.modeDetails,
        payment_type: form.paymentType,
        amount: form.amount,
      });

      setForm({
        date: "",
        mode: "Cheque",
        modeDetails: "",
        paymentType: "Advance",
        amount: "",
      });

      fetchSummary();
    } catch (err) {
      console.error("Add payment failed:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (row) => {
    setEditingId(row.payment_id);
    setEditForm({
      date: row.payment_date || "",
      mode: row.payment_mode || "Cheque",
      modeDetails: row.mode_details || "",
      paymentType: row.payment_type || "Advance",
      amount: row.amount || "",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEditing = async (paymentId) => {
    if (!editForm.date || !editForm.amount) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setSavingRow(true);

      await api.put(`/payment-manage/update/${paymentId}`, {
        date: editForm.date,
        mode: editForm.mode,
        mode_details: editForm.modeDetails,
        payment_type: editForm.paymentType,
        amount: editForm.amount,
      });

      setEditingId(null);
      setEditForm(null);

      fetchSummary();
    } catch (err) {
      console.error("Update payment failed:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update.");
    } finally {
      setSavingRow(false);
    }
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  return (
    <div className="ml-[68px] pt-16 p-6 bg-[#F4F7FB] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="border rounded-md p-2 hover:bg-gray-100"
          >
            <ArrowLeft size={18} />
          </button>

          <div>
            <h1 className="text-2xl font-semibold">Manage Payment</h1>
            <p className="text-sm text-gray-500">
              Deal ID : {opfId} &nbsp;|&nbsp; Lead ID : {leadId || "—"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Mode</th>
              <th className="text-left p-3">Mode Details</th>
              <th className="text-left p-3">Payment Type</th>
              <th className="text-left p-3">Amount</th>
              <th className="text-left p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {/* Input Row (new entry) */}
            <tr className="border-t">
              <td className="p-2">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <select
                  value={form.mode}
                  onChange={(e) => setForm({ ...form, mode: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                >
                  <option>Cheque</option>
                  <option>Cash</option>
                  <option>Other</option>
                </select>
              </td>

              <td className="p-2">
                <input
                  type="text"
                  placeholder="e.g. Cheque No / UTR"
                  value={form.modeDetails}
                  onChange={(e) =>
                    setForm({ ...form, modeDetails: e.target.value })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <select
                  value={form.paymentType}
                  onChange={(e) =>
                    setForm({ ...form, paymentType: e.target.value })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                >
                  {PAYMENT_TYPES.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>
              </td>

              <td className="p-2">
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <button
                  onClick={submit}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                >
                  <Save size={16} />
                  {loading ? "Saving..." : "Submit"}
                </button>
              </td>
            </tr>

            {/* Existing Rows (editable) */}
            {rows.map((row) => {
              const isEditing = editingId === row.payment_id;

              return (
                <tr key={row.payment_id} className="border-t">
                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.date}
                        onChange={(e) =>
                          setEditForm({ ...editForm, date: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{row.payment_date}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <select
                        value={editForm.mode}
                        onChange={(e) =>
                          setEditForm({ ...editForm, mode: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      >
                        <option>Cheque</option>
                        <option>Cash</option>
                        <option>Other</option>
                      </select>
                    ) : (
                      <span className="p-1">{row.payment_mode}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.modeDetails}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            modeDetails: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{row.mode_details || "—"}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <select
                        value={editForm.paymentType}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            paymentType: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      >
                        {PAYMENT_TYPES.map((opt) => (
                          <option key={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          row.payment_type === "Advance"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {row.payment_type}
                      </span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.amount}
                        onChange={(e) =>
                          setEditForm({ ...editForm, amount: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{formatCurrency(row.amount)}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveEditing(row.payment_id)}
                          disabled={savingRow}
                          className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm"
                        >
                          <Save size={14} />
                          {savingRow ? "Saving..." : "Save"}
                        </button>

                        <button
                          onClick={cancelEditing}
                          className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(row)}
                        className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-xs text-gray-500 font-medium">Total Amount</p>
          <p className="text-lg font-semibold text-gray-800 mt-1">
            {formatCurrency(totalAmount)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-xs text-gray-500 font-medium">Advance Amount</p>
          <p className="text-lg font-semibold text-blue-600 mt-1">
            {formatCurrency(advanceTotal)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-xs text-gray-500 font-medium">Part Payment</p>
          <p className="text-lg font-semibold text-amber-600 mt-1">
            {formatCurrency(partTotal)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <p className="text-xs text-gray-500 font-medium">Balance</p>
          <p
            className={`text-lg font-semibold mt-1 ${
              balance <= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {formatCurrency(balance)}
          </p>
        </div>
      </div>
    </div>
  );
}